import { SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Checkbox, CheckboxProps, Col, Flex, Input, Row } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UIConfig } from '../../enum';
import { useUserSessionData } from '../../router/selectors';
import { searchASCII } from '../../util';
import { FallbackComponent } from '../Fallback';
import { ColumnConfigIcon } from '../Icon';
import { ErrorMessageBox } from '../MessageBox';
import { Modal } from '../Modal';
import { useUIConfigAPI, useUpdateUIConfigAPI } from './selectors';
import { SortableList } from './SortableList';

interface Option {
  label: string;
  value: string;
  selected?: boolean;
  order?: number | null;
  id: string;
}

export interface ColumnsTypePermission<T> extends ColumnType<T> {
  permissions?: string[];
}

export interface PermissionColumn {
  key: string;
  permissions: string[];
}

interface ColumnConfigProps {
  type: UIConfig;
  permissionColumn?: PermissionColumn[];
}

const ItemsCard = styled(Flex)`
  height: calc(100vh - 340px);
  overflow-y: auto;
`;

const CheckboxGroupContainer = styled(Flex)`
  flex-direction: column;
  & .ant-checkbox-group {
    display: flex;
    gap: 4px;
  }
  & .ant-checkbox-wrapper {
    padding-top: 6px;
    padding-bottom: 6px;
  }
  & .ant-checkbox-group-item {
    display: flex;
    width: 100%;
  }
`;

/**
 * Column configuration modal component
 * - Allows users to select, reorder, and save column configurations
 * - Supports permission-based column visibility
 * - Includes search and bulk selection features
 */
export const ColumnConfig: FC<ColumnConfigProps> = ({
  type,
  permissionColumn,
}) => {
  const [searchText, setSearchText] = useState<string>('');
  const [uiConfigData, getUIConfigData, clearUIConfigData] =
    useUIConfigAPI(type);
  const [uiConfigUpdateData, updateUIConfigData, clearUpdateUIConfigData] =
    useUpdateUIConfigAPI();
  const [columnsState, setColumnsState] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const userSessionData = useUserSessionData();

  // Fetch initial configuration data
  useEffect(() => {
    getUIConfigData();
    return clearUIConfigData;
  }, [clearUIConfigData, getUIConfigData]);

  // Handle successful configuration updates
  useEffect(() => {
    if (uiConfigUpdateData.data) {
      handleClose();
      getUIConfigData();
      return clearUpdateUIConfigData;
    }
  }, [clearUpdateUIConfigData, getUIConfigData, uiConfigUpdateData.data]);

  // Initialize columns when modal opens
  useEffect(() => {
    if (uiConfigData.data && open) {
      const formattedColumns = uiConfigData.data.columns
        .map((col) => ({
          ...col,
          // order: null,
          id: col.name,
          label: col.displayName,
          value: col.name,
        }))
        .filter((col) => {
          const column = permissionColumn?.find(
            (colItem) => colItem.key === col.name
          );
          const hasPermission = column?.permissions?.every((permission) =>
            userSessionData?.permissions.includes(permission)
          );
          // In case column no config permission OR exists permission and must include
          return !column || hasPermission;
        });
      setColumnsState(formattedColumns);
      setCheckedList(
        formattedColumns.filter((col) => col.selected).map((col) => col.value)
      );
    }
  }, [open, uiConfigData, permissionColumn, userSessionData?.permissions]);

  // Filter columns based on search text
  const optionsFiltered = columnsState.map((col) => {
    const included = searchASCII(searchText, col.label ?? '');
    const hiddenStyle = !included ? { style: { display: 'none' } } : {};
    return {
      ...col,
      ...hiddenStyle,
    };
  });

  // Memoized values for checkbox states
  const checkedDefaultList = useMemo(
    () => columnsState.map((col) => col.value),
    [columnsState]
  );
  const checkAll = useMemo(
    () => checkedList.length === columnsState.length,
    [checkedList, columnsState]
  );
  const indeterminate = useMemo(
    () => checkedList.length > 0 && checkedList.length < columnsState.length,
    [checkedList, columnsState]
  );

  // Get currently selected columns
  const selectColumns = useMemo(() => {
    const filtered: Option[] = [];
    checkedList.forEach((col) => {
      const column = columnsState.find((item) => item.value === col);
      if (column) {
        filtered.push(column);
      }
    });

    return filtered;
  }, [checkedList, columnsState]);

  // Handle individual column selection
  const onChange = (id: string, checked: boolean) => {
    let list = [];
    if (checked) {
      list = [...checkedList, id];
    } else {
      list = checkedList.filter((item) => item !== id);
    }
    setCheckedList(list);
    const updatedColumnsState = columnsState.map((col) => ({
      ...col,
      order: null,
      selected: list.includes(col.value),
    }));

    setColumnsState(updatedColumnsState);
  };

  // Handle select/deselect all columns
  const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
    setCheckedList(e.target.checked ? checkedDefaultList : []);
    const updatedColumnsState = columnsState.map((col) => ({
      ...col,
      order: null,
      selected: checkedDefaultList.includes(col.value),
    }));

    setColumnsState(updatedColumnsState);
  };

  // Reset modal state on close
  const handleClose = () => {
    setCheckedList([]);
    setSearchText('');
    clearUpdateUIConfigData();
    setOpen(false);
  };

  // Handle column reordering
  const handleSortChange = useCallback(
    (sortedList: Option[]) => {
      const updatedColumnsState = [...columnsState].map((col) => {
        const sortedItem = sortedList.find((item) => item.value === col.value);
        if (sortedItem) {
          return {
            ...col,
            order: sortedItem.order,
            selected: sortedItem.selected,
          };
        }
        return { ...col, order: null, selected: false };
      });
      setColumnsState(updatedColumnsState);
    },
    [columnsState]
  );

  // Handle column deletion from sortable list
  const onDeleteOnSort = useCallback((sortedList: Option[]) => {
    setCheckedList(sortedList.map((item) => item.value));
  }, []);

  // Debounced search handler
  const searchRef = useRef(
    debounce((text: string) => setSearchText(text), 500)
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    searchRef.current(e.target.value);
  };

  // Submit updated column configuration
  const handleSubmit = () => {
    const valueColumnsUpdate = selectColumns
      .filter((item) => item.selected)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((item) => item.value);

    const payload = {
      configType: type,
      columns: valueColumnsUpdate,
    };
    updateUIConfigData(payload);
  };

  return (
    <>
      <Button
        size="large"
        icon={<ColumnConfigIcon />}
        onClick={() => setOpen(true)}
      />
      <Modal
        title="UI Config"
        destroyOnClose
        open={open}
        centered
        maskClosable={false}
        onCancel={handleClose}
        onOk={handleSubmit}
        cancelButtonProps={{ size: 'large' }}
        okButtonProps={{ size: 'large' }}
        okText="Save"
        width={700}
      >
        <FallbackComponent
          overlay
          requesting={!!uiConfigUpdateData.requesting}
        />
        <ErrorMessageBox errors={[uiConfigUpdateData.error]} />
        <Row gutter={8}>
          <Col span={12}>
            <Flex vertical gap={8}>
              <Input
                className="w-full h-10"
                size="large"
                placeholder="Search column"
                onChange={handleSearchChange}
                prefix={<SearchOutlined />}
              />
              <ItemsCard
                vertical
                gap={8}
                className="p-3 border rounded-md [&::-webkit-scrollbar]:w-[14px]
                [&::-webkit-scrollbar-track]:rounded-full
                [&::-webkit-scrollbar-track]:bg-gray-100
                [&::-webkit-scrollbar-thumb]:rounded-full
                [&::-webkit-scrollbar-thumb]:bg-gray-300"
              >
                <Checkbox
                  indeterminate={indeterminate}
                  onChange={onCheckAllChange}
                  checked={checkAll}
                >
                  All fields
                </Checkbox>
                <CheckboxGroupContainer>
                  {optionsFiltered.map((col) => (
                    <Checkbox
                      key={col.value}
                      style={col.style}
                      checked={checkedList.includes(col.value)}
                      onChange={(event) =>
                        onChange(col.value, event.target.checked)
                      }
                    >
                      {col.label}
                    </Checkbox>
                  ))}
                </CheckboxGroupContainer>
              </ItemsCard>
            </Flex>
          </Col>
          <Col span={12}>
            <Flex vertical className="border h-full rounded-md">
              <Flex
                justify="space-between"
                align="center"
                className="px-3 py-1 border-b"
              >
                <span>{checkedList.length} columns selected</span>
                <Button type="link" onClick={() => setCheckedList([])}>
                  Clear
                </Button>
              </Flex>
              <ItemsCard
                className="p-1 [&::-webkit-scrollbar]:w-[14px]
                [&::-webkit-scrollbar-track]:rounded-full
                [&::-webkit-scrollbar-track]:bg-gray-100
                [&::-webkit-scrollbar-thumb]:rounded-full
                [&::-webkit-scrollbar-thumb]:bg-gray-300
                overflow-x-hidden"
                vertical
              >
                <SortableList
                  items={selectColumns}
                  onChange={handleSortChange}
                  onDelete={onDeleteOnSort}
                />
              </ItemsCard>
            </Flex>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
